import React, { ClipboardEventHandler, FormEvent, forwardRef, useMemo, useRef, useState } from 'react';
import classes from './TitleBlock.module.scss';
import inputClasses from '../../../components/forms/form.module.scss';
import { CSSTransition } from 'react-transition-group';
import { useDidMount } from '../../../hooks/useDidMount';
import classNames from 'classnames';
import { Tooltip } from '../../../components/actions/Tooltip';
import { GroupPostAttributesType } from '../../../models/post-editor';
import { debounce } from 'lodash';
import { idGenerator } from '../../../helpers/idGenerator';
import DOMPurify from 'dompurify';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { ErrorMessageSeverity } from '../../../constants/form';
import { countSymbols } from '../../../helpers/countSymbols';
import { POST_EDITOR_ERROR_CLASS } from '../../../constants/styleConstants';

export type TitleBlockViewProps = {
  //
  title: GroupPostAttributesType['content']['title'];
  updateAction: (e: FormEvent<HTMLDivElement>, current: GroupPostAttributesType['content']['title']) => void;
  handlePaste: ClipboardEventHandler<HTMLDivElement>;
  maxLength: number;
  errorMessages: { severity: ErrorMessageSeverity | null; messages: JSX.Element };
};

export const TitleBlockView = forwardRef<HTMLDivElement, TitleBlockViewProps>((props, ref): JSX.Element => {
  const { title, updateAction, maxLength, errorMessages, handlePaste } = props;
  const { severity, messages } = errorMessages;
  const defaultValue = useRef(title);

  const [mount, setMount] = useState<boolean>(false);
  const cssRef = useRef<HTMLDivElement>(null);

  useDidMount(() => {
    // we ned to setMount to true, since we wnat to trigger animation (fade) only when the component has been completely mounted.
    setMount(true);
    return () => {
      setMount(false);
    };
  });

  const tooltipId = useMemo(() => idGenerator('TooltipTitleBlock'), []);
  const charCountClasses = classNames(classes.TitleLabel, 'cell shrink', {
    [classes.WarningState]: title && countSymbols(title) <= maxLength && countSymbols(title) > maxLength - 20,
    [classes.AlertState]: title && countSymbols(title) > maxLength
  });

  const validationClasses = classNames(classes.TitleBlock, {
    [classes.TitleBlockError]: severity === ErrorMessageSeverity.error,
    [classes.TitleBlockWarning]: severity === ErrorMessageSeverity.warning,
    [POST_EDITOR_ERROR_CLASS]: severity === ErrorMessageSeverity.error
  });

  return (
    <CSSTransition in={mount} timeout={300} classNames={'fade'} nodeRef={cssRef} unmountOnExit>
      <div className={validationClasses} ref={cssRef}>
        <div className={'grid-x align-middle align-justify'}>
          <div className={'cell shrink'}>
            <div className={'grid-x align-middle align-left'}>
              <div className={classNames(classes.TitleLabel, 'cell', 'shrink')}>Title</div>
              <div className={'cell shrink'}>
                <Tooltip
                  target={`#${tooltipId}`}
                  position={'right'}
                  pointer
                  type={'inlined'}
                  autoZIndex={false}
                  style={{ zIndex: scssVars['z-index-overmodal'] }}>
                  <div>
                    Some social networks allow you to enter a title for your post:
                    <ul>
                      <li>Pinterest posts</li>
                      <li>Facebook Video posts</li>
                    </ul>
                  </div>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className={'cell shrink'}>
            <div className={'grid-x align-middle'}>
              {!!severity ? (
                <div className={classNames('cell shrink', classes.ValidationTooltip)}>{messages}</div>
              ) : null}
              <div className={charCountClasses}>{title ? 100 - countSymbols(title) : 100}</div>
            </div>
          </div>
        </div>
        <div className={'grid-x margin-top-small-16'}>
          <div className={'cell small-12 relative'}>
            <div
              ref={ref}
              onInput={debounce(e => updateAction(e, title), 350)}
              className={classNames(inputClasses.FormInputs, inputClasses.large, classes.ValueField)}
              data-placeholder={'Type a title for your Post'}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(defaultValue.current || '') }}
              contentEditable={true}
              onPaste={handlePaste}
            />
          </div>
        </div>
      </div>
    </CSSTransition>
  );
});

TitleBlockView.displayName = 'TitleBlockView';
